import React from 'react';
import PropTypes from 'prop-types';
import Card from './Card';

/**
 * Table component following UI design specifications
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Table component
 */
const Table = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = '暂无数据',
  emptyIcon,
  emptyAction,
  className = '',
  variant = 'default',
  ...props
}) => {
  if (loading) {
    return (
      <Card variant={variant} className={className}>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent"></div>
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card variant={variant} className={className}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          {emptyIcon || (
            <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-5m-9 0h5" />
            </svg>
          )}
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {emptyMessage}
          </h3>
          {emptyAction}
        </div>
      </Card>
    );
  }

  return (
    <Card variant={variant} padding="none" className={className} {...props}>
      <div className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            {/* Table Header */}
            <thead className="bg-gray-50/50">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={column.key || index}
                    scope="col"
                    className={`
                      px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider
                      ${column.className || ''}
                    `}
                    style={{ width: column.width }}
                  >
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-gray-50">
              {data.map((row, rowIndex) => (
                <tr 
                  key={row.id || rowIndex}
                  className="hover:bg-emerald-50/50 transition-colors duration-200"
                >
                  {columns.map((column, colIndex) => (
                    <td 
                      key={`${rowIndex}-${column.key || colIndex}`}
                      className={`
                        px-6 py-4 whitespace-nowrap text-sm
                        ${column.cellClassName || ''}
                      `}
                    >
                      {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string.isRequired,
    render: PropTypes.func,
    width: PropTypes.string,
    className: PropTypes.string,
    cellClassName: PropTypes.string
  })).isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  emptyIcon: PropTypes.node,
  emptyAction: PropTypes.node,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'modern', 'glass', 'floating', 'simple'])
};

export default Table; 